<script lang="ts" setup>
import emblaCarouselVue from 'embla-carousel-vue'
type Step = {
  title: string
  subTitle: string
  text: string
  callToAction: Record<string, unknown>
  media: Record<string, unknown>
}

type Props = {
  steps: Step[]
}

const _props = defineProps<{ data: Props; index: number }>()

const emit = defineEmits(['active-index', 'click'])

const [emblaRef, emblaApi] = emblaCarouselVue({ align: 'start' })

const activeIndex = ref(0)

watch(activeIndex, () => {
  emit('active-index', activeIndex.value)
})

onMounted(() => {
  if (emblaApi.value) {
    const togglePrevNextBtnsState = () => {
      activeIndex.value = emblaApi.value?.selectedScrollSnap() || 0
    }

    emblaApi.value
      .on('select', togglePrevNextBtnsState)
      .on('init', togglePrevNextBtnsState)
      .on('reInit', togglePrevNextBtnsState)

    // Scroll to initial index on mount
    emblaApi.value.scrollTo(_props.index, 0)
    activeIndex.value = _props.index
  }
})

// Watch for prop index changes
watch(
  () => _props.index,
  (newIndex) => {
    if (emblaApi.value) {
      emblaApi.value.scrollTo(newIndex, 0)
      activeIndex.value = newIndex
    }
  },
)

const { isMobileOrTablet } = useDevice()
const { width: innerWidth } = useWindowSize()

const isMobile = computed(() => {
  if (import.meta.server) return isMobileOrTablet
  return innerWidth.value < 900
})
</script>

<template>
  <div>
    <div ref="emblaRef" class="embla overflow-hidden pb-5 lg:z-[2]">
      <div class="embla__container relative z-[2] w-full">
        <button
          v-for="(item, idx) in data.steps"
          :key="idx"
          class="embla__slide cursor-pointer text-left opacity-40 transition-opacity"
          :class="{ 'opacity-100': activeIndex === idx }"
          @click="
            () => {
              activeIndex = idx
              emblaApi?.scrollTo(idx, 0)
              emit('click')
            }
          "
        >
          <client-only>
            <ElementsStepContentOverlay :data="item" class="relative" />
          </client-only>
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped lang="postcss">
.embla {
  --slide-spacing: var(--grid-gap);
  --slide-size: var(--cols-4);
}
.embla__viewport {
  overflow: hidden;
}
.embla__container {
  display: flex;
  touch-action: pan-y pinch-zoom;
}

.embla__slide {
  transform: translate3d(0, 0, 0);
  flex: 0 0 var(--slide-size);
  min-width: 0;
  padding-left: var(--grid-bleed);

  &:last-of-type {
    padding-right: var(--grid-bleed);
  }
}
</style>
